import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StatusBadge } from '../components/Badges';
import { Receipt, ExternalLink, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function TransactionsPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [activeTxn, setActiveTxn] = useState(null);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTransactions();
    const sessionId = params.get('session_id');
    if (sessionId && !sessionId.includes('{')) {
      pollPaymentStatus(sessionId);
    }
  }, []);

  const fetchTransactions = async () => {
    try {
      const res = await api.get('/transactions/my');
      setTransactions(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 8) {
      toast.error('Payment status check timed out');
      return;
    }
    setPolling(true);
    try {
      const res = await api.get(`/payments/stripe/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful! Lead unlocked.');
        setPolling(false);
        fetchTransactions();
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Payment session expired');
        setPolling(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      setPolling(false);
      toast.error('Error checking payment status');
    }
  };

  const handleDispute = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/transactions/${activeTxn.transaction_id}/dispute`, {
        reason,
        description,
      });
      toast.success('Dispute raised. LeadXchange will review.');
      setDisputeOpen(false);
      setReason(''); setDescription('');
      fetchTransactions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to raise dispute');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-24 text-gray-500 font-mono text-sm">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Transactions</h1>
        <p className="text-sm text-gray-600 mt-1">All purchases and sales with dispute history.</p>

        {polling && (
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-md p-4 flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full spinner" />
            <span className="text-sm text-blue-900">Verifying payment status...</span>
          </div>
        )}

        <div className="mt-8">
          {transactions.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm flex flex-col items-center gap-2">
              <Receipt className="w-8 h-8 text-gray-300" />
              No transactions yet
            </div>
          ) : (
            <div className="space-y-3" data-testid="transactions-list">
              {transactions.map(t => {
                const isBuyer = t.buyer_id === user.user_id;
                return (
                  <div key={t.transaction_id} className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-mono text-xs text-gray-400">{t.transaction_id}</div>
                        <div className="font-heading text-base font-semibold mt-1">
                          {isBuyer ? 'Purchase' : 'Sale'} · Lead {t.lead_id}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={t.payment_status} />
                        {t.dispute_status && <StatusBadge status={t.dispute_status} />}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Amount</div>
                        <div className="font-mono font-semibold mt-0.5">${t.amount.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Method</div>
                        <div className="font-medium mt-0.5 capitalize">{t.payment_method}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Unlocked</div>
                        <div className="font-medium mt-0.5">{t.lead_unlocked ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />Yes</span> : 'No'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider">Date</div>
                        <div className="font-mono text-xs mt-0.5">{new Date(t.created_at).toLocaleDateString()}</div>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-2">
                      {t.lead_unlocked && (
                        <Button variant="outline" size="sm" onClick={() => navigate(`/leads/${t.lead_id}`)} data-testid={`view-lead-${t.lead_id}`}>
                          <ExternalLink className="w-3.5 h-3.5 mr-1" /> View Lead
                        </Button>
                      )}
                      {(t.payment_status === 'paid' || t.payment_status === 'refunded') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await api.get(`/transactions/${t.transaction_id}/invoice`);
                              const inv = res.data;
                              const win = window.open('', '_blank');
                              if (!win) return;
                              // SEC: escape every interpolated string. Invoice fields
                              // include buyer-controlled names/emails/notes/aliases —
                              // never inject them into an HTML template raw.
                              const esc = (v) => String(v ?? '')
                                .replace(/&/g, '&amp;')
                                .replace(/</g, '&lt;')
                                .replace(/>/g, '&gt;')
                                .replace(/"/g, '&quot;')
                                .replace(/'/g, '&#39;');
                              const money = (n, c) => `$${Number(n || 0).toFixed(2)} ${esc(c)}`;
                              const b = inv.buyer || {};
                              const s = inv.seller || {};
                              const l = inv.lead || {};
                              const a = inv.amounts || {};
                              const sup = inv.supplier || {};
                              const html = `<!doctype html><html><head><title>Tax Invoice ${esc(inv.invoice_number)}</title>
                                <style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:720px;margin:32px auto;padding:24px;color:#111827}
                                h1{font-size:22px;margin:0 0 4px}.mono{font-family:ui-monospace,monospace}.muted{color:#6b7280;font-size:12px}
                                table{width:100%;border-collapse:collapse;margin-top:16px;font-size:14px}
                                td{padding:8px 0;border-bottom:1px solid #f3f4f6}.total td{font-weight:600;border-top:2px solid #111827;border-bottom:none}
                                .header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:24px;padding-bottom:16px;border-bottom:1px solid #e5e7eb}
                                .brand{color:#0055FF;font-weight:600;letter-spacing:2px;font-size:11px;text-transform:uppercase}
                                @media print{body{margin:0}}</style></head><body>
                                <div class="header"><div><div class="brand">LeadXchange · Tax Invoice</div>
                                <h1>${esc(inv.invoice_number)}</h1><div class="muted">Issued ${esc(new Date(inv.invoice_date).toLocaleDateString())}</div></div>
                                <div style="text-align:right"><div style="font-weight:600">${esc(sup.legal_name)}</div>
                                <div class="muted">Trading as ${esc(sup.trading_as)}</div>
                                <div class="mono muted">ABN ${esc(sup.abn)}</div></div></div>
                                <table><tr><td class="muted">Bill To</td><td>${esc(b.name || b.alias || '')} (${esc(b.alias)})<br><span class="muted mono">${esc(b.email || '')}</span>${b.company_name ? '<br>'+esc(b.company_name) : ''}${b.abn ? '<br>ABN '+esc(b.abn) : ''}</td></tr>
                                <tr><td class="muted">Description</td><td>${esc(l.category)} lead — ${esc(l.region)}<br><span class="mono muted">${esc(l.lead_id)} · ${esc(l.digital_title || '')}</span></td></tr>
                                <tr><td class="muted">Seller</td><td class="mono">${esc(s.alias)}</td></tr>
                                <tr><td class="muted">Subtotal (ex-GST)</td><td>${esc(money(a.subtotal_ex_gst, a.currency))}</td></tr>
                                <tr><td class="muted">GST 10%</td><td>${esc(money(a.gst_amount, a.currency))}</td></tr>
                                <tr class="total"><td>Total (inc GST)</td><td>${esc(money(a.total_inclusive, a.currency))}</td></tr>
                                ${inv.status === 'refunded' ? '<tr><td class="muted">Refund</td><td style="color:#b91c1c">REFUNDED · '+esc(inv.stripe_refund_id||'')+'</td></tr>' : ''}
                                </table><p class="muted" style="margin-top:24px">${esc(inv.notes)}</p></body></html>`;
                              win.document.open();
                              win.document.write(html);
                              win.document.close();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || 'Invoice unavailable');
                            }
                          }}
                          data-testid={`invoice-${t.transaction_id}`}
                        >
                          <Receipt className="w-3.5 h-3.5 mr-1" /> Tax Invoice
                        </Button>
                      )}
                      {isBuyer && t.payment_status === 'paid' && !t.dispute_status && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setActiveTxn(t); setDisputeOpen(true); }}
                          className="border-red-200 text-red-700 hover:bg-red-50"
                          data-testid={`dispute-${t.transaction_id}`}
                        >
                          <AlertTriangle className="w-3.5 h-3.5 mr-1" /> Raise Dispute
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Dialog open={disputeOpen} onOpenChange={setDisputeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Raise Dispute</DialogTitle>
            <DialogDescription>
              LeadXchange will review the lead source proof and decide on refund or release.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDispute} className="space-y-4">
            <div>
              <Label>Reason</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Lead unreachable" required data-testid="dispute-reason-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Provide details..." required data-testid="dispute-description-input" />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#0055FF] hover:bg-[#0044CC] text-white" data-testid="submit-dispute-btn">Submit Dispute</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
